/**
 * Semantic View / Composite View YAML 에서 검색용 메타 추출.
 *
 * KBNL-74 후속 — 유사 SV 찾기 모달의 picker / 변환 결과 검색에 사용.
 *
 * 사용 시점:
 *  - CompositeViewer 의 카드별 검색: 조립된 CV YAML 의 sub-SV 별 메타 추출
 *  - SqlConverter / Workbench 의 composite picker: 변환 결과 YAML 에서 SV 선택지 표시
 *  - SqlConverter / Workbench 의 single SV 결과 검색: 변환 결과 SV 를 seed 로 검색
 *
 * 위 시점에서는 SV doc 이 Firestore 에 저장되어 있지 않거나(변환 결과)
 * 다중 SV 가 한 YAML 에 들어 있어서(CV) backend 의 /sv/extracted-keywords
 * endpoint 를 부를 수 없다. 그래서 FE 에서 파싱해 처리한다.
 *
 * 코드 리뷰: 이전엔 `semantic_view:` 문자열 split 으로 블록을 나눴는데,
 * (a) `semantic_views:` 리스트 형식(리소스/레거시 CV)을 아예 못 뽑고,
 * (b) nested `source: - semantic_view: SV_REF`(SV 참조)에서 블록이 잘려
 *     dimensions/measures 가 사라지고,
 * (c) multi-doc(`---`) 구분자가 첫 블록에 섞이고,
 * (d) block scalar `description: |` 를 본문이 아니라 '|' 로 잘못 추출하는
 * 결함이 있었다. → js-yaml 구조 파싱으로 교체. 파싱 실패 시에만 정규식 fallback.
 */
import yaml from 'js-yaml'

/**
 * SV dict 의 source 리스트에서 물리 테이블명만 추출.
 * nested `- semantic_view: SV_X`(SV 참조)는 table 이 없으므로 자연 제외된다.
 */
function tablesFromSv(sv) {
  const out = []
  const src = sv && sv.source
  if (Array.isArray(src)) {
    for (const s of src) {
      if (s && typeof s === 'object' && s.table != null) {
        out.push(String(s.table).trim())
      }
    }
  }
  return out
}

/**
 * SV dict → 요약 { name, description, tables, yaml }.
 * yaml: 이 SV 만 `semantic_view:` 단일 래퍼로 재직렬화한 원문 (seed 검색용).
 */
function summarizeSv(sv) {
  if (!sv || typeof sv !== 'object') return null
  const name = (sv.name != null ? String(sv.name) : '').trim()
  if (!name) return null
  const description = (sv.description != null ? String(sv.description) : '').trim()
  let snippet = ''
  try {
    // lineWidth:-1 → 긴 줄을 접지 않음 (expr/condition 보존). backend 파서는
    // `semantic_view:` 단일 래퍼를 이미 지원하므로 이 형식이면 안전하게 파싱된다.
    snippet = yaml.dump({ semantic_view: sv }, { lineWidth: -1, noRefs: true })
  } catch {
    snippet = ''
  }
  return { name, description, tables: tablesFromSv(sv), yaml: snippet }
}

/**
 * YAML 에서 sub-SV 별 (name, description, tables, yaml) 요약 추출.
 * 지원 형식:
 *   - `semantic_view:` 단일 래퍼 (변환 결과 single SV)
 *   - `semantic_views:` 리스트 (composite / 리소스 · 레거시)
 *   - 래퍼 없는 top-level SV dict
 *   - multi-doc(`---`) — 각 문서를 순회
 *
 * @param {string} yamlStr
 * @returns {Array<{name: string, description: string, tables: string[], yaml: string}>}
 */
export function extractViewSummariesFromYaml(yamlStr) {
  if (!yamlStr || !yamlStr.trim()) return []

  let docs
  try {
    docs = yaml.loadAll(yamlStr)   // multi-doc(`---`) 대응
  } catch {
    return extractViewSummariesRegex(yamlStr)   // 파싱 실패 시 fallback
  }

  const out = []
  const push = (sv) => { const s = summarizeSv(sv); if (s) out.push(s) }

  for (const doc of docs) {
    if (!doc || typeof doc !== 'object') continue
    if (doc.semantic_view && typeof doc.semantic_view === 'object') {
      push(doc.semantic_view)
    } else if (Array.isArray(doc.semantic_views)) {
      doc.semantic_views.forEach(push)
    } else if (doc.semantic_views && typeof doc.semantic_views === 'object') {
      push(doc.semantic_views)
    } else if (doc.name) {
      // 래퍼 없는 단일 SV dict
      push(doc)
    }
  }
  return out
}

/**
 * 파서가 실패할 때만 쓰는 정규식 fallback (best-effort, 근사).
 * 구조 파싱이 안 되는 깨진 YAML 에서도 최소한 이름은 잡아 준다.
 */
function extractViewSummariesRegex(yamlStr) {
  const out = []
  const blocks = yamlStr.split(/^\s*semantic_view\s*:/m)
  for (const block of blocks.slice(1)) {
    const nameM = block.match(/^\s*name:\s*(.+)$/m)
    if (!nameM) continue
    const name = nameM[1].trim().replace(/^["']|["']$/g, '')
    const descM = block.match(/^\s*description:\s*(.+)$/m)
    let description = descM ? descM[1].trim().replace(/^["']|["']$/g, '') : ''
    if (description === '|' || description === '>') description = ''   // block scalar 지시자면 버림
    const tableMatches = [...block.matchAll(/^\s*table:\s*(.+)$/gm)]
    const tables = tableMatches.map(m => m[1].trim().replace(/^["']|["']$/g, ''))
    out.push({ name, description, tables, yaml: 'semantic_view:' + block })
  }
  return out
}
